import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AlarmOffRoundedIcon from '@mui/icons-material/AlarmOffRounded'
import AssignmentIcon from '@mui/icons-material/Assignment'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import LabelIcon from '@mui/icons-material/Label'
import TransformIcon from '@mui/icons-material/Transform'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'

import { useAuthenticatedUser } from '../../../../auth/hooks/useAuthenticatedUser'
import type { OptionsDialogData } from '../../types/OptionsDialogData'
import type { Project } from '../../types/Project'

interface Props {
  project: Project
  anchorEl?: HTMLElement
  onClose: () => void
  onNavigateToOpdrachten: (project: Project) => void
  onOpenDialog: (data: OptionsDialogData) => void
  selectableLabels: string[]
}

export const ProjectOptionsMenu = ({
  project,
  anchorEl,
  onClose,
  onNavigateToOpdrachten,
  onOpenDialog,
  selectableLabels,
}: Props) => {
  const {
    deelnemer,
    delegerende,
    canViewCombiUitvoeringMelden,
    canViewQuickScan,
    canViewActiefMelden,
    canViewUpdateLabels,
    canCreateHerplanningKlant,
    organisatie,
    canCreateGebiedswissel,
  } = useAuthenticatedUser()
  const { verantwoordelijken } = project
  const isVerantwoordelijke =
    !!organisatie && verantwoordelijken.includes(organisatie)

  const isActiehouderQuickscan =
    project.type === 'Aanvraag' &&
    project.acties &&
    ((deelnemer && project.acties.quickscan.includes(deelnemer)) ||
      (delegerende && project.acties.quickscan.includes(delegerende)))

  const isContractNetbeheerder =
    project.netbeheerdersContract &&
    (project.netbeheerdersContract.includes(deelnemer as string) ||
      project.netbeheerdersContract.includes(delegerende as string))

  const canUserHerplan =
    project.statusText === 'Intake' && canCreateHerplanningKlant

  const canUpdateLabels = canViewUpdateLabels && selectableLabels.length > 0

  const canUserGebiedswissel =
    canCreateGebiedswissel && isVerantwoordelijke && project.type === 'Aanvraag'

  const handleOpdrachtenClick = () => {
    onClose()
    onNavigateToOpdrachten(project)
  }

  const handleOptionClick = (type: OptionsDialogData['type']) => {
    onClose()
    onOpenDialog({
      project,
      type,
    })
  }
  const handleLabelsClick = () => {
    onOpenDialog({
      project,
      selectableLabels,
      type: 'labels',
    })
    onClose()
  }

  const handleGebiedswisselClick = () => {
    onOpenDialog({
      project,
      type: 'gebiedswissel',
    })
    onClose()
  }

  return (
    <Menu
      id="menu-appbar"
      data-testid="menu"
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={Boolean(anchorEl)}
      onClose={onClose}
    >
      <MenuItem onClick={handleOpdrachtenClick}>
        <ListItemIcon>
          <AssignmentIcon />
        </ListItemIcon>
        <ListItemText primary="Opdrachten" />
      </MenuItem>

      {canViewCombiUitvoeringMelden && isContractNetbeheerder && (
        <MenuItem onClick={() => handleOptionClick('combiuitvoering')}>
          <ListItemIcon>
            <AccountTreeIcon />
          </ListItemIcon>
          <ListItemText primary="Mee in combi" />
        </MenuItem>
      )}

      {canViewQuickScan && isContractNetbeheerder && isActiehouderQuickscan && (
        <MenuItem onClick={() => handleOptionClick('quickscan')}>
          <ListItemIcon>
            <AccountTreeIcon />
          </ListItemIcon>
          <ListItemText primary="Quickscan" />
        </MenuItem>
      )}

      {canViewActiefMelden && (
        <MenuItem onClick={() => handleOptionClick('actief')}>
          <ListItemIcon>
            <AlarmOffRoundedIcon />
          </ListItemIcon>
          <ListItemText
            primary={project.isActive ? 'Deactiveren' : 'Activeren'}
          />
        </MenuItem>
      )}
      {canUserHerplan && (
        <MenuItem onClick={() => handleOptionClick('herplanning_klant')}>
          <ListItemIcon>
            <EventRepeatIcon />
          </ListItemIcon>
          <ListItemText primary={'Herplannen'} />
        </MenuItem>
      )}

      {canUpdateLabels && (
        <MenuItem onClick={handleLabelsClick}>
          <ListItemIcon>
            <LabelIcon />
          </ListItemIcon>
          <ListItemText primary="Wijzig labels" />
        </MenuItem>
      )}

      {canUserGebiedswissel && (
        <MenuItem onClick={handleGebiedswisselClick}>
          <ListItemIcon>
            <TransformIcon />
          </ListItemIcon>
          <ListItemText primary="Wissel gebied" />
        </MenuItem>
      )}
    </Menu>
  )
}
